import React, { useEffect, useId, useState } from 'react';
import {
  X,
  PanelLeft,
  Smartphone,
  MessageCircle,
  Star,
  SlidersVertical,
  Bell,
  ChartNoAxesColumn,
  ChartLine,
  Globe,
  Check,
} from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { useTutorial } from '../context/TutorialContext';
import { useTheme } from '../context/ThemeContext';
import TutorialIllustration from './TutorialIllustration';
import MultiSelectCommoditySelector from '../pages/components/MultiSelectCommoditySelector';

// Quick-pick chips shown under the commodity dropdown. Deliberately NOT
// translated: these must match the commodity names the API returns (English)
// or toggling them wouldn't line up with a dropdown selection.
const QUICK_PICK_COMMODITIES = ['Strawberries', 'Cherries', 'Tomatoes', 'Avocados', 'Grapes'];

// Stable keys; labels come from `tutorial.steps.personalize.workOptions.*`.
const WORK_AREAS = ['grower', 'distributor', 'foodServices', 'other'];

// Chips shown before collapsing behind a "+N more" toggle. Roughly two rows at
// the modal's width — selecting all ~111 commodities would otherwise render a
// chip per commodity and blow the modal past the viewport.
const CHIP_LIMIT = 10;

/** Toggleable pill used for both the commodity quick-picks and work areas. */
const Chip = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`flex h-[34px] items-center gap-[6px] rounded-full border px-5 font-['Inter'] text-[14px] font-medium transition-colors ${
      selected
        ? 'border-bloom-green bg-[#F0FAF4] text-bloom-green dark:bg-bloom-green/10'
        : 'border-[#E6E9E4] bg-white text-[#282A29] hover:border-[#C9CEC6] dark:border-[#3A3D3C] dark:bg-transparent dark:text-[#f0f5f2]'
    }`}
  >
    {label}
    {selected && <Check size={14} strokeWidth={2.5} />}
  </button>
);

/**
 * Onboarding tour shown on a user's first authenticated session.
 *
 * Steps are data-driven; every entry reads its copy from `tutorial.steps.<key>`.
 * The body is composed from whichever of these the step declares, in order:
 *
 *   variant / image  artwork — a placeholder SVG, or a real asset if `image` is
 *                    set (add `image: '/assets/<file>.png'` to swap). The box is
 *                    198×134 unless the step overrides `artworkWidth` /
 *                    `artworkHeight`; give it the asset's natural size so the
 *                    art isn't scaled down inside it. An asset referenced from
 *                    <img> can't read the app's theme class, so a step whose art
 *                    is drawn for light backgrounds also sets `imageDark` with
 *                    the dark-mode export; without one the light art is reused.
 *   bullets          number of entries to read from the `bullets` array; each
 *                    may wrap emphasis in <b>…</b>
 *   note             renders `noteTitle` + `noteBody` as a centred sub-block
 *   cards            three tiles, paired positionally with the `cards` labels.
 *                    An entry is either a lucide icon component (drawn at 46px
 *                    on a tinted panel) or a string path to a screenshot, which
 *                    fills the panel edge to edge.
 *
 * `descriptionWidth` caps the description/note column so copy breaks across
 * lines the way the design does (defaults to 520px).
 */
export const TUTORIAL_STEPS = [
  {
    key: 'welcome',
    image: '/assets/welcome-tutorial.png',
    imageDark: '/assets/welcome-tutorial.svg',
    artworkWidth: 520,
    artworkHeight: 290,
  },
  {
    key: 'biq',
    cards: [PanelLeft, Smartphone, MessageCircle],
    descriptionWidth: 560,
  },
  {
    key: 'map',
    // Product screenshots rather than icons. Each is exported at the panel's
    // own size (213×120), so they sit in it without being cropped.
    cards: [
      '/assets/weather-alerts.png',
      '/assets/map-overview.png',
      '/assets/shipment-details-tooltip.png',
    ],
    descriptionWidth: 560,
  },
  { key: 'terminal', bullets: 2, cards: [ChartNoAxesColumn, ChartLine, Globe] },
  {
    key: 'alerts',
    bullets: 2,
    note: true,
    cards: [Star, SlidersVertical, Bell],
    descriptionWidth: 620,
  },
  // "personalize" (commodity picker + work area) is parked to keep the tour at
  // the 5 steps the design calls for. To bring it back, un-comment this and bump
  // `totalSteps` in TutorialContext to match. Everything it depends on (the
  // `step.interactive` block below, Chip, WORK_AREAS, QUICK_PICK_COMMODITIES,
  // MultiSelectCommoditySelector) is left in place and is inert while no step
  // declares `interactive`.
  // { key: 'personalize', interactive: true },
];

const TutorialModal = () => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const titleId = useId();
  const [selectedCommodities, setSelectedCommodities] = useState([]);
  const [workArea, setWorkArea] = useState(null);
  const [showAllChips, setShowAllChips] = useState(false);
  const {
    isTutorialVisible,
    currentStep,
    totalSteps,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
  } = useTutorial();

  // Lock background scroll and wire Esc / arrow-key navigation while open.
  useEffect(() => {
    if (!isTutorialVisible) return undefined;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') skipTutorial();
      else if (e.key === 'ArrowRight') nextStep();
      else if (e.key === 'ArrowLeft') previousStep();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isTutorialVisible, skipTutorial, nextStep, previousStep]);

  if (!isTutorialVisible) return null;

  const stepIndex = Math.min(Math.max(currentStep, 1), totalSteps) - 1;
  const step = TUTORIAL_STEPS[stepIndex];
  const isLastStep = stepIndex === TUTORIAL_STEPS.length - 1;
  const hasArtwork = Boolean(step.image || step.variant);
  const artworkSrc = (isDark && step.imageDark) || step.image;
  const artworkWidth = step.artworkWidth ?? 198;
  const artworkHeight = step.artworkHeight ?? 134;
  const descriptionWidth = step.descriptionWidth ?? 520;
  const base = `tutorial.steps.${step.key}`;

  // Quick picks first, then anything else the user picked from the dropdown.
  const allChips = [...new Set([...QUICK_PICK_COMMODITIES, ...selectedCommodities])];
  const hiddenChipCount = Math.max(0, allChips.length - CHIP_LIMIT);
  const visibleChips = showAllChips ? allChips : allChips.slice(0, CHIP_LIMIT);

  return (
    // BiQ launcher z-[2000], MapSidebar z-[2100], nav drawer z-[2200]/[2210] — slicing the tour dialog
    <div className="fixed inset-0 z-[2300] flex items-center justify-center p-4">
      {/* Backdrop is intentionally NOT click-to-dismiss — matching the previous
          tutorial. Leaving the tour should be a deliberate "Skip tour" click. */}
      <div className="absolute inset-0 bg-[#5D625F]/60" aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // min-h rather than h: steps 1–2 land at exactly 560px as specced, but
        // taller steps (bullets + note + cards) grow instead of scrolling.
        // Interactive steps drop overflow-hidden so the commodity dropdown's
        // absolutely-positioned panel isn't clipped by the modal edge.
        className={`relative z-10 flex w-full max-w-[760px] max-h-full flex-col rounded-[20px] border border-[#E6E9E4] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] dark:border-[#3A3D3C] dark:bg-[#2A2929] sm:min-h-[560px] ${
          step.interactive ? '' : 'overflow-hidden'
        }`}
      >
        {/* ── Header: step counter, skip, progress bars ───────────────── */}
        <div className="shrink-0 px-5 pt-5 sm:px-6 sm:pt-6">
          <div className="flex items-center justify-between">
            <span className="font-['Inter'] text-[12.5px] font-bold leading-none tracking-[0.3px] text-bloom-green">
              {t('tutorial.stepLabel', {
                current: stepIndex + 1,
                total: totalSteps,
              })}
            </span>

            {/* Label and icon share one colour via currentColor, so they can
                never drift apart. Icon is 18px because lucide draws the X at
                half its box (9px), which matches the cap height of 13px text. */}
            <button
              type="button"
              onClick={skipTutorial}
              className="flex items-center gap-[20px] rounded text-[#828382] transition-colors hover:text-[#282A29] dark:hover:text-[#f0f5f2]"
              aria-label={t('tutorial.skipTour')}
            >
              <span className="font-['Inter'] text-[13px] font-semibold leading-none">
                {t('tutorial.skipTour')}
              </span>
              <X size={18} strokeWidth={2} />
            </button>
          </div>

          <div
            className="mt-4 flex gap-[5px]"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={totalSteps}
            aria-valuenow={stepIndex + 1}
          >
            {TUTORIAL_STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`h-[4px] flex-1 rounded-[3px] transition-colors duration-300 ${
                  i <= stepIndex
                    ? 'bg-bloom-green'
                    : 'bg-[#E6E9E4] dark:bg-[#3A3D3C]'
                }`}
              />
            ))}
          </div>
        </div>

        {/* ── Content: title, artwork, description, bullets, cards ─────── */}
        {/* m-auto rather than justify-center: it still centres when there is
            spare room, but won't clip the top of taller steps once the area
            starts scrolling. */}
        <div
          className={`flex flex-1 flex-col px-5 py-4 text-center sm:px-6 ${
            step.interactive ? 'overflow-visible' : 'overflow-y-auto'
          }`}
        >
          <div className="m-auto flex w-full flex-col items-center">
            <h2
              id={titleId}
              className="font-['Inter'] text-[22px] font-extrabold text-[#282A29] dark:text-[#f0f5f2] sm:text-[26px]"
            >
              {t(`${base}.title`)}
            </h2>

            {/* aspect-ratio rather than a fixed height: below the artwork's
                natural width the box shrinks with it instead of leaving a band
                of empty space under the art. */}
            {hasArtwork && (
              <div
                className="mt-3 flex max-w-full items-center justify-center"
                style={{
                  width: artworkWidth,
                  aspectRatio: `${artworkWidth} / ${artworkHeight}`,
                }}
              >
                {artworkSrc ? (
                  <img
                    src={artworkSrc}
                    alt={t(`${base}.imageAlt`)}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <TutorialIllustration variant={step.variant} className="h-full w-full" />
                )}
              </div>
            )}

            {i18n.exists(`${base}.description`) && (
              <p
                style={{ maxWidth: descriptionWidth }}
                className={`font-['Inter'] text-[16px] font-normal leading-[22px] text-[#5D625F] dark:text-[#b9bfbb] ${
                  hasArtwork ? 'mt-10' : 'mt-4'
                }`}
              >
                {t(`${base}.description`)}
              </p>
            )}

            {step.bullets > 0 && (
              <ul className="mt-7 space-y-3 text-left">
                {Array.from({ length: step.bullets }, (_, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-[7px] h-[7px] w-[7px] shrink-0 rounded-[3.5px] bg-bloom-green" />
                    <span className="font-['Inter'] text-[16px] font-normal leading-[22px] text-[#282A29] dark:text-[#f0f5f2]">
                      <Trans
                        i18nKey={`${base}.bullets.${i}`}
                        components={{ b: <strong className="font-bold" /> }}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {step.note && (
              <div style={{ maxWidth: descriptionWidth }} className="mt-8">
                <p className="font-['Inter'] text-[16px] font-bold leading-[22px] text-[#282A29] dark:text-[#f0f5f2]">
                  {t(`${base}.noteTitle`)}
                </p>
                <p className="mt-1 font-['Inter'] text-[16px] font-normal leading-[22px] text-[#282A29] dark:text-[#f0f5f2]">
                  {t(`${base}.noteBody`)}
                </p>
              </div>
            )}

            {step.cards?.length > 0 && (
              <div className="mt-8 grid w-full max-w-[684px] grid-cols-3 gap-[17px]">
                {step.cards.map((card, i) => (
                  <div
                    key={i}
                    // No divider rule — the two panels are separated purely by their
                    // background tones. overflow-hidden clips the fills to the 12px
                    // radius so they follow the rounded corners.
                    className="flex h-[162px] flex-col overflow-hidden rounded-[12px] border border-[#E6E9E4] dark:border-[#3A3D3C]"
                  >
                    <div className="flex flex-1 items-center justify-center overflow-hidden bg-[#EEF1EC] dark:bg-[#313433]">
                      {typeof card === 'string' ? (
                        // Screenshots are wider than the panel, so they crop from
                        // the top — the part of a UI shot worth showing. Empty alt:
                        // the label directly below already names the tile.
                        <img src={card} alt="" className="h-full w-full object-cover object-top" />
                      ) : (
                        React.createElement(card, {
                          size: 46,
                          strokeWidth: 1.75,
                          className: 'text-bloom-green',
                        })
                      )}
                    </div>
                    <div className="bg-[#E3F5EB] px-2 py-[14px] font-['Inter'] text-[12.5px] font-semibold leading-none tracking-normal text-[#282A29] dark:bg-[#3A3D3C] dark:text-[#f0f5f2]">
                      {t(`${base}.cards.${i}`)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {step.interactive && (
              <>
                <p className="mt-10 font-['Inter'] text-[16px] font-bold leading-[22px] text-[#282A29] dark:text-[#f0f5f2]">
                  {t(`${base}.commodityQuestion`)}
                </p>

                <div className="mt-3 w-full max-w-[306px]">
                  <MultiSelectCommoditySelector
                    values={selectedCommodities}
                    onChange={setSelectedCommodities}
                    placeholder={t(`${base}.commodityPlaceholder`)}
                  />
                </div>

                {/* Quick picks plus anything chosen from the dropdown, so a
                    dropdown selection always shows up as a chip too. Collapsed
                    to CHIP_LIMIT by default; expanding scrolls inside a fixed
                    height rather than growing the modal. */}
                <div
                  className={`mt-3 flex w-full flex-wrap justify-center gap-[10px] ${
                    showAllChips ? 'max-h-[152px] overflow-y-auto' : ''
                  }`}
                >
                  {visibleChips.map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      selected={selectedCommodities.includes(name)}
                      onClick={() =>
                        setSelectedCommodities((prev) =>
                          prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
                        )
                      }
                    />
                  ))}

                  {(hiddenChipCount > 0 || showAllChips) && (
                    <button
                      type="button"
                      onClick={() => setShowAllChips((prev) => !prev)}
                      className="h-[34px] rounded-full border border-[#E6E9E4] bg-white px-5 font-['Inter'] text-[14px] font-medium text-[#5D625F] transition-colors hover:border-[#C9CEC6] dark:border-[#3A3D3C] dark:bg-transparent dark:text-[#b9bfbb]"
                    >
                      {showAllChips
                        ? t('tutorial.showLess')
                        : t('tutorial.showMore', { n: hiddenChipCount })}
                    </button>
                  )}
                </div>

                <p className="mt-10 font-['Inter'] text-[16px] font-bold leading-[22px] text-[#282A29] dark:text-[#f0f5f2]">
                  {t(`${base}.workQuestion`)}
                </p>

                <div className="mt-3 flex flex-wrap justify-center gap-[10px]">
                  {WORK_AREAS.map((key) => (
                    <Chip
                      key={key}
                      label={t(`${base}.workOptions.${key}`)}
                      selected={workArea === key}
                      onClick={() => setWorkArea((prev) => (prev === key ? null : key))}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Footer: step dots, back / next ──────────────────────────── */}
        <div className="flex shrink-0 items-center justify-between px-5 pb-5 sm:px-6 sm:pb-6">
          <div className="flex items-center gap-[6px]">
            {TUTORIAL_STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`h-[8px] transition-all duration-300 ${
                  i === stepIndex
                    ? 'w-[22px] rounded-[7.5px] bg-bloom-green'
                    : 'w-[8px] rounded-[4px] bg-[#D7DBD5] dark:bg-[#4A4D4B]'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={previousStep}
                className="h-[42px] rounded-lg px-4 font-['Inter'] text-[14px] font-semibold text-[#5D625F] transition-colors hover:bg-[#F0F5F2] dark:text-[#b9bfbb] dark:hover:bg-[#3d403e]"
              >
                {t('tutorial.buttons.back')}
              </button>
            )}

            <button
              type="button"
              onClick={isLastStep ? () => completeTutorial(selectedCommodities) : nextStep}
              className="h-[42px] rounded-lg bg-bloom-green px-7 font-['Inter'] text-[14px] font-bold text-white transition-colors hover:bg-bloom-green-hover"
            >
              {isLastStep ? t('tutorial.buttons.finish') : t('tutorial.buttons.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;
